import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { BiGroup, BiReport, biService } from '../services/biService';
import { canSeeBiReport } from '../utils/biAccess';
import { useAuth } from './auth';

export { filterBiReports, groupBiReports } from '../utils/biCatalog';

// Prefixo das chaves do catálogo. `invalidateQueries(BI_CATALOG_KEY)` atinge
// o catálogo de qualquer usuário.
export const BI_CATALOG_KEY = 'bi-catalog';

// O catálogo traz favoritos e últimos acessos de quem está logado: a chave
// inclui o usuário para quem entrar depois na mesma aba não ver os dados do
// anterior.
function useBiCatalogKey() {
  const { user } = useAuth();

  return [BI_CATALOG_KEY, user?.id];
}

export function useBiCatalog() {
  return useQuery(useBiCatalogKey(), biService.listCatalog, {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Relatórios que o usuário logado pode abrir, na ordem da API
// (grupo, ordem definida no admin, nome).
export function useVisibleBiReports() {
  const { user } = useAuth();
  const query = useBiCatalog();

  const reports = useMemo(
    () => (query.data || []).filter(report => canSeeBiReport(user, report)),
    [query.data, user],
  );

  // Falha numa atualização em segundo plano não apaga a lista já carregada.
  return { ...query, isError: query.isError && !query.data, reports };
}

export const BI_GROUPS_KEY = 'bi-groups';

// Cor, ícone e ordem de cada grupo, indexados pelo nome do grupo.
export function useBiGroups() {
  const query = useQuery(BI_GROUPS_KEY, biService.listGroups, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const byName = useMemo(() => {
    const map: Record<string, BiGroup | undefined> = {};

    (query.data || []).forEach(group => {
      map[group.name] = group;
    });

    return map;
  }, [query.data]);

  return { ...query, groups: query.data || [], byName };
}

export function useToggleBiFavorite() {
  const queryClient = useQueryClient();
  const key = useBiCatalogKey();

  return useMutation(
    async (report: BiReport) => {
      if (report.isFavorite) {
        await biService.removeFavorite(report.id);
      } else {
        await biService.addFavorite(report.id);
      }
    },
    {
      // Atualiza a estrela na hora e desfaz se a API recusar.
      onMutate: async (report: BiReport) => {
        await queryClient.cancelQueries(key);

        const previous = queryClient.getQueryData<BiReport[]>(key);

        queryClient.setQueryData<BiReport[]>(key, current =>
          (current || []).map(item =>
            item.id === report.id
              ? { ...item, isFavorite: !report.isFavorite }
              : item,
          ),
        );

        return { previous };
      },
      onError: (_err, _report, context) => {
        const ctx = context as { previous?: BiReport[] } | undefined;

        if (ctx?.previous) {
          queryClient.setQueryData(key, ctx.previous);
        }
      },
    },
  );
}
